import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { readSheet as readXlsxSheet } from 'read-excel-file/browser';
import { API_BASE_URL } from '../config/api';
import './SearchPage.css';

// CSV 表头别名:把各种导出工具(Sales Navigator / Apollo / PhantomBuster / 手工 Excel)的列名归一化
const CSV_HEADER_ALIASES = {
  linkedin_url: ['linkedin_url', 'profile_url', 'url', 'linkedin', 'profile', 'linkedin_profile', 'linkedin_profile_url'],
  name: ['name', 'full_name', 'fullname'],
  first_name: ['first_name', 'firstname', 'given_name', 'first'],
  last_name: ['last_name', 'lastname', 'family_name', 'surname', 'last'],
  headline: ['headline', 'title', 'job_title', 'position', 'current_title', 'role'],
  location: ['location', 'city', 'country', 'region', 'geo_location'],
  company: ['company', 'current_company', 'organization', 'employer', 'company_name'],
  public_identifier: ['public_identifier', 'public_id', 'slug', 'username'],
};

// 标准 CSV parser:处理引号字段、转义双引号("")、CRLF、BOM、空行
function parseCSV(text) {
  if (!text) return [];
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // strip BOM (Excel 导出常见)

  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } // 转义引号
        else { inQuotes = false; }
      } else {
        field += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field);
        field = '';
      } else if (c === '\r') {
        // 跳过,等 \n 处理
      } else if (c === '\n') {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else {
        field += c;
      }
    }
  }
  // 收尾
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter(r => r.some(c => c.trim().length > 0));
}

// 把 parseCSV 的结果归一化成统一形状:{ linkedin_url, name, headline, location, company, public_identifier }
function normalizeCsvRows(rawRows) {
  if (rawRows.length === 0) {
    return { rows: [], error: 'CSV 为空' };
  }
  const headers = rawRows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, ''));
  const colIdx = {};
  for (const [key, aliases] of Object.entries(CSV_HEADER_ALIASES)) {
    const idx = headers.findIndex(h => aliases.includes(h));
    if (idx >= 0) colIdx[key] = idx;
  }
  if (colIdx.linkedin_url === undefined) {
    return {
      rows: [],
      error: `CSV 缺少 LinkedIn URL 列。识别的别名:${CSV_HEADER_ALIASES.linkedin_url.join(' / ')}。当前表头:${headers.join(', ') || '(空)'}`,
    };
  }

  const dataRows = rawRows.slice(1);
  const seen = new Set();
  const rows = [];
  let dupCount = 0;
  let invalidUrlCount = 0;

  const get = (row, key) => (colIdx[key] !== undefined ? (row[colIdx[key]] || '').trim() : '');

  for (const raw of dataRows) {
    let url = get(raw, 'linkedin_url');
    if (!url) continue;
    if (!/linkedin\.com\/in\//i.test(url)) { invalidUrlCount++; continue; }
    url = url.split('?')[0].replace(/\/$/, ''); // 去 query + 末尾斜杠
    if (seen.has(url)) { dupCount++; continue; }
    seen.add(url);

    // name:优先 name/full_name,没有则拼 first_name + last_name
    let name = get(raw, 'name');
    if (!name) {
      const f = get(raw, 'first_name');
      const l = get(raw, 'last_name');
      name = [f, l].filter(Boolean).join(' ').trim();
    }

    // public_identifier:优先 CSV 给的,没有就从 URL 提
    let publicId = get(raw, 'public_identifier');
    if (!publicId) {
      const m = url.match(/linkedin\.com\/in\/([^/?#]+)/i);
      if (m) publicId = m[1];
    }

    rows.push({
      linkedin_url: url,
      profile_url: url,
      name: name || null,
      headline: get(raw, 'headline') || null,
      location: get(raw, 'location') || null,
      company: get(raw, 'company') || null,
      public_identifier: publicId || null,
      _source: 'csv',
    });
  }

  return {
    rows,
    error: null,
    stats: {
      total: dataRows.length,
      imported: rows.length,
      withName: rows.filter(r => r.name).length,
      withoutName: rows.filter(r => !r.name).length,
      duplicates: dupCount,
      invalidUrls: invalidUrlCount,
    },
  };
}

function SearchPage({ accountId }) {
  const [searchMethod, setSearchMethod] = useState('url'); // 'url' | 'nlp' | 'csv'
  const [searchUrl, setSearchUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [maxResults, setMaxResults] = useState(10);
  const [maxResultsInput, setMaxResultsInput] = useState('10'); // 用于输入框显示
  // 人脉度数过滤(只对 NLP 模式生效):默认全选 = 不过滤,跟原行为一致
  const [networkDistance, setNetworkDistance] = useState([1, 2, 3]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // NLP 搜索确认流程状态
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [parsedParams, setParsedParams] = useState(null);
  const [convertedParams, setConvertedParams] = useState(null);
  const [originalQuery, setOriginalQuery] = useState('');
  const [linkedinSearchUrl, setLinkedinSearchUrl] = useState('');
  const [urlParams, setUrlParams] = useState(null);
  const [resolvedFilters, setResolvedFilters] = useState(null);
  const [unsupportedFilters, setUnsupportedFilters] = useState([]);
  const [accountCapabilities, setAccountCapabilities] = useState(null);
  const [searchExecutionMode, setSearchExecutionMode] = useState('url');
  const [parameterSearchReason, setParameterSearchReason] = useState('');
  
  // Leads List 相关状态
  const [showAddToList, setShowAddToList] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [addingToList, setAddingToList] = useState(false);

  // === 打分相关状态 ===
  const [hiringNeed, setHiringNeed] = useState('');
  const [scoring, setScoring] = useState(false);
  // scores: { 'https://linkedin.com/in/xxx': { score, reason, criteria_breakdown, status } }
  const [scores, setScores] = useState({});
  // statusByUrl: 'crawling' | 'scoring' | 'completed' | 'failed' | 'timeout'
  const [statusByUrl, setStatusByUrl] = useState({});
  const [parsedCriteria, setParsedCriteria] = useState(null);
  const [sortByScore, setSortByScore] = useState(false);
  const [scoringError, setScoringError] = useState('');
  const scoringAbortRef = useRef(null);

  // === CSV 导入相关状态 ===
  const [csvFileName, setCsvFileName] = useState('');
  const [csvParsedRows, setCsvParsedRows] = useState([]);   // 归一化后的行
  const [csvStats, setCsvStats] = useState(null);
  const [csvError, setCsvError] = useState('');
  const [csvParsing, setCsvParsing] = useState(false);
  const csvInputRef = useRef(null);

  // 选中要添加到 Leads List 的候选人 (linkedin_url Set)。默认全选,打完分后用户可去掉低分。
  const [selectedUrls, setSelectedUrls] = useState(new Set());
  const toggleSelected = (url) => {
    if (!url) return;
    setSelectedUrls(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };
  const selectAll = () => {
    setSelectedUrls(new Set(results.map(p => p.linkedin_url || p.profile_url).filter(Boolean)));
  };
  const deselectAll = () => setSelectedUrls(new Set());

  // 阈值选择:用 [_x_] 分 + 一键覆盖选中
  const [scoreThreshold, setScoreThreshold] = useState(80);
  const selectByScore = () => {
    const matched = results
      .map(p => p.linkedin_url || p.profile_url)
      .filter(url => url && (scores[url]?.score ?? -1) >= scoreThreshold);
    setSelectedUrls(new Set(matched));
  };

  // 用搜索结果 + hiring_need 调后端 SSE 透传,实时渲染分数
  const handleStartScoring = async () => {
    if (!hiringNeed.trim()) {
      setScoringError('请输入招聘需求(hiring need)');
      return;
    }
    if (results.length === 0) {
      setScoringError('没有可打分的搜索结果');
      return;
    }

    setScoring(true);
    setScoringError('');
    setScores({});
    setStatusByUrl({});
    setParsedCriteria(null);
    setSortByScore(false);

    const candidates = results
      .map(p => ({
        name: p.name || '',
        linkedin_url: p.linkedin_url || p.profile_url || '',
      }))
      .filter(c => c.linkedin_url);

    const controller = new AbortController();
    scoringAbortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE_URL}/score/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hiring_need: hiringNeed, candidates }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`打分服务返回 ${res.status}: ${text.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE 事件以 \n\n 分隔
        let sepIdx;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          // 一个 event 可能多行,取以 'data: ' 开头的那行
          const dataLine = rawEvent.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          let event;
          try {
            event = JSON.parse(dataLine.slice(6));
          } catch (e) {
            console.warn('SSE parse failed:', dataLine.slice(0, 100));
            continue;
          }

          if (event.type === 'start') {
            setParsedCriteria(event.parsed_criteria || null);
          } else if (event.type === 'progress') {
            // 用 name 反查 linkedin_url(progress 不带 url)
            const target = results.find(p => (p.name || '') === event.name);
            const url = target?.linkedin_url || target?.profile_url;
            if (url) {
              setStatusByUrl(prev => ({ ...prev, [url]: event.status }));
            }
          } else if (event.type === 'completed') {
            const url = event.linkedin_url;
            if (url) {
              setScores(prev => ({
                ...prev,
                [url]: {
                  score: event.score,
                  reason: event.reason,
                  criteria_breakdown: event.criteria_breakdown,
                  job_title: event.job_title,
                  company: event.company,
                  location: event.location,
                  status: event.status,
                },
              }));
              setStatusByUrl(prev => ({ ...prev, [url]: event.status || 'completed' }));
              // 回填结构化字段到 results,只填原本为空的位置(让 CSV-only-URL 场景下打分能补全 leads 字段)
              setResults(prev => prev.map(p => {
                const pUrl = p.linkedin_url || p.profile_url;
                if (pUrl !== url) return p;
                return {
                  ...p,
                  name: p.name || event.name || null,
                  headline: p.headline || event.job_title || null,
                  company: p.company || event.company || null,
                  location: p.location || event.location || null,
                };
              }));
            }
          } else if (event.type === 'done') {
            // 全部完成,自动按分数从高到低排序(用户可点切换)
            setSortByScore(true);
          } else if (event.type === 'error') {
            setScoringError(event.message || '打分失败');
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Scoring aborted by user');
      } else {
        console.error('Scoring error:', err);
        setScoringError(err.message || '打分链路出错');
      }
    } finally {
      setScoring(false);
      scoringAbortRef.current = null;
    }
  };

  const handleStopScoring = () => {
    if (scoringAbortRef.current) {
      scoringAbortRef.current.abort();
    }
  };

  // 颜色:>=80 绿,60-79 黄,<60 灰
  const scoreColor = (s) => {
    if (s >= 80) return '#16a34a';
    if (s >= 60) return '#ca8a04';
    return '#6b7280';
  };

  // === 文件解析(CSV / xlsx)===
  const handleCsvFile = async (file) => {
    if (!file) return;
    setCsvError('');
    setCsvStats(null);
    setCsvParsedRows([]);
    setCsvFileName(file.name);

    // 5MB 软上限,避免浏览器解析阻塞
    if (file.size > 5 * 1024 * 1024) {
      setCsvError(`文件过大 (${(file.size / 1024 / 1024).toFixed(1)} MB),请控制在 5MB 以内`);
      return;
    }

    const lowerName = file.name.toLowerCase();
    const isXlsx = lowerName.endsWith('.xlsx') || file.type.includes('spreadsheetml');
    const isCsv = lowerName.endsWith('.csv') || file.type.includes('csv') || file.type === 'text/plain';
    if (!isXlsx && !isCsv) {
      setCsvError(`不支持的文件类型:${file.name}。请上传 .csv 或 .xlsx`);
      return;
    }

    setCsvParsing(true);
    try {
      let rawRows;
      if (isXlsx) {
        // read-excel-file v9: readSheet(file, 1) 拿第一个 sheet 的行;单元格可能是 Date/number/null,统一转字符串
        const xlsxRows = await readXlsxSheet(file, 1);
        rawRows = xlsxRows.map(r => r.map(c => (c == null ? '' : String(c))));
      } else {
        const text = await file.text();
        rawRows = parseCSV(text);
      }
      const { rows, error, stats } = normalizeCsvRows(rawRows);
      if (error) {
        setCsvError(error);
        setCsvParsing(false);
        return;
      }
      if (rows.length === 0) {
        setCsvError(`${isXlsx ? 'Excel' : 'CSV'} 中没有有效的 LinkedIn URL`);
        setCsvParsing(false);
        return;
      }
      setCsvParsedRows(rows);
      setCsvStats(stats);
    } catch (err) {
      console.error('File parse error:', err);
      setCsvError(`解析失败:${err.message || err}`);
    } finally {
      setCsvParsing(false);
    }
  };

  const handleCsvImport = () => {
    if (csvParsedRows.length === 0) return;
    setError('');
    setSuccess('');
    setResults(csvParsedRows);
    setSelectedUrls(new Set(csvParsedRows.map(r => r.linkedin_url).filter(Boolean)));
    sessionStorage.setItem('search_results', JSON.stringify(csvParsedRows));
    // 清掉旧的打分结果,避免 URL 巧合命中旧分数
    setScores({});
    setStatusByUrl({});
    setParsedCriteria(null);
    setSortByScore(false);
    setScoringError('');
    setSuccess(`成功导入 ${csvParsedRows.length} 个候选人。${csvStats?.withoutName ? `提示:${csvStats.withoutName} 个缺少姓名,模板里的 {first_name} 将无法渲染,可考虑先打分以补全数据。` : ''}`);
  };

  const handleCsvReset = () => {
    setCsvFileName('');
    setCsvParsedRows([]);
    setCsvStats(null);
    setCsvError('');
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const handleSearchByUrl = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setResults([]);

    try {
      const response = await axios.post(`${API_BASE_URL}/linkedin/search`, {
        account_id: accountId,
        search_url: searchUrl,
        max_results: maxResults
      });

      if (response.data.profiles && response.data.profiles.length > 0) {
        setResults(response.data.profiles);
        // 默认全选,用户可在打完分后取消勾选低分的
        setSelectedUrls(new Set(response.data.profiles.map(p => p.linkedin_url || p.profile_url).filter(Boolean)));
        // Save results to sessionStorage for Campaign use
        sessionStorage.setItem('search_results', JSON.stringify(response.data.profiles));
        setSuccess(`成功提取 ${response.data.profiles.length} 个人才资料！可以在Campaign页面添加这些人到outreach工作流。`);
      } else {
        setError('未找到符合条件的人才');
      }
    } catch (err) {
      setError(err.response?.data?.message || '搜索失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 步骤1: 解析自然语言查询
  const handleSearchByNLP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setResults([]);

    try {
      const response = await axios.post(`${API_BASE_URL}/linkedin/search/nlp/parse`, {
        account_id: accountId,
        query: searchQuery,
        network_distance: networkDistance
      });

      if (response.data.success) {
        setOriginalQuery(response.data.original_query);
        setParsedParams(response.data.parsed_params);
        setConvertedParams(response.data.converted_params);
        setLinkedinSearchUrl(response.data.linkedin_search_url || '');
        setUrlParams(response.data.url_params || null);
        setResolvedFilters(response.data.resolved_filters || null);
        setUnsupportedFilters(response.data.unsupported_filters || []);
        setAccountCapabilities(response.data.account_capabilities || null);
        setSearchExecutionMode(response.data.search_execution_mode || 'url');
        setParameterSearchReason(response.data.parameter_search_reason || '');
        setShowConfirmation(true);
        // 预填打分用的招聘描述,保留用户自定义内容
        if (!hiringNeed.trim()) {
          setHiringNeed(response.data.original_query || '');
        }
        setSuccess('查询解析成功！请确认搜索条件。');
      }
    } catch (err) {
      setError(err.response?.data?.message || '查询解析失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 步骤2: 执行确认后的搜索
  const handleConfirmSearch = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setResults([]);

    try {
      const response = await axios.post(`${API_BASE_URL}/linkedin/search/nlp/execute`, {
        account_id: accountId,
        linkedin_search_url: linkedinSearchUrl,
        converted_params: convertedParams,
        search_execution_mode: searchExecutionMode,
        max_results: maxResults
      });

      if (response.data.profiles && response.data.profiles.length > 0) {
        setResults(response.data.profiles);
        setSelectedUrls(new Set(response.data.profiles.map(p => p.linkedin_url || p.profile_url).filter(Boolean)));
        // Save results to sessionStorage for Campaign use
        sessionStorage.setItem('search_results', JSON.stringify(response.data.profiles));
        const totalAvailable = Number(response.data.total_available);
        const totalHint = Number.isFinite(totalAvailable) && totalAvailable > 0
          ? `，LinkedIn 约有 ${totalAvailable} 条结果`
          : '';
        const paginationHint = response.data.pagination_incomplete
          ? '；Unipile 分页提前结束，实际提取数量少于请求数量'
          : '';
        setSuccess(`成功提取 ${response.data.profiles.length} 个人才资料${totalHint}${paginationHint}！可以在Campaign页面添加这些人到outreach工作流。`);
        setShowConfirmation(false);
      } else {
        setError('未找到符合条件的人才');
      }
    } catch (err) {
      setError(err.response?.data?.message || '搜索失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 取消确认，返回编辑
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setParsedParams(null);
    setConvertedParams(null);
    setOriginalQuery('');
    setLinkedinSearchUrl('');
    setUrlParams(null);
    setResolvedFilters(null);
    setUnsupportedFilters([]);
    setAccountCapabilities(null);
    setSearchExecutionMode('url');
    setParameterSearchReason('');
    setError('');
    setSuccess('');
  };

  // 编辑搜索参数
  const handleEditParam = (paramName, newValue) => {
    setParsedParams(prev => ({
      ...prev,
      [paramName]: newValue
    }));
  };

  const downloadResults = () => {
    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `linkedin_profiles_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // 获取参数的中文标签
  const getParamLabel = (key) => {
    const labels = {
      keywords: '关键词',
      titles: '职位',
      location: '地点',
      locations: '地点',
      company: '公司',
      current_companies: '当前公司',
      past_companies: '过往公司',
      industry: '行业',
      industries: '行业',
      title: '职位',
      school: '学校',
      schools: '学校',
      service_categories: '服务类别',
      skills: '技能',
      job_functions: '职能',
      employment_types: '雇佣类型',
      actively_hiring: '正在招聘',
      open_to_volunteer: '开放志愿服务',
      is_verified: '已验证 Profile',
      years_of_experience_min: '最少工作年限',
      years_of_experience_max: '最多工作年限',
      profile_language: 'Profile语言',
      network_distance: '人脉度数',
      preferred_service: '搜索方式'
    };
    return labels[key] || key;
  };

  const getSearchServiceLabel = (service) => {
    const normalized = String(service || 'classic').toLowerCase();
    const labels = {
      classic: 'LinkedIn Classic People',
      sales_navigator: 'LinkedIn Sales Navigator',
      recruiter: 'LinkedIn Recruiter'
    };
    return labels[normalized] || service;
  };

  const getAccountCapabilityLabel = (capabilities) => {
    const level = String(capabilities?.level || 'unknown').toLowerCase();
    const labels = {
      premium: 'Premium',
      sales_navigator: 'Sales Navigator',
      recruiter: 'Recruiter',
      classic: 'Basic / Classic',
      unknown: '未确认'
    };
    return labels[level] || capabilities?.level || '未确认';
  };

  const getExecutionModeLabel = (mode) => {
    return mode === 'parameters' ? 'Unipile 参数搜索' : 'LinkedIn URL 搜索';
  };

  // 格式化Profile语言显示
  const formatProfileLanguage = (languages) => {
    const languageNames = {
      zh: '中文',
      en: '英文',
      ja: '日文',
      ko: '韩文',
      es: '西班牙文',
      fr: '法文',
      de: '德文'
    };
    if (Array.isArray(languages)) {
      return languages.map(lang => languageNames[lang] || lang).join(', ');
    }
    return String(languages);
  };

  // 加载 Leads Lists
  const loadLists = async () => {
    if (!accountId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/leads-lists`, {
        params: { account_id: accountId }
      });
      if (response.data.success) {
        setLists(response.data.lists);
      }
    } catch (error) {
      console.error('Load lists error:', error);
    }
  };

  useEffect(() => {
    if (accountId) {
      loadLists();
    }
  }, [accountId]);

  // 创建新的 Leads List
  const handleCreateList = async () => {
    if (!newListName.trim()) {
      setError('请输入列表名称');
      return null;
    }

    setAddingToList(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/leads-lists`, {
        account_id: accountId,
        name: newListName.trim(),
        description: newListDescription.trim() || null
      });

      if (response.data.success) {
        const newList = response.data.list;
        setLists([...lists, newList]);
        setSelectedListId(newList.id);
        setNewListName('');
        setNewListDescription('');
        setSuccess('列表创建成功！');
        setTimeout(() => setSuccess(''), 2000);
        setAddingToList(false);
        return newList.id; // 返回新列表的 ID
      } else {
        setAddingToList(false);
        setError('创建列表失败');
        return null;
      }
    } catch (error) {
      console.error('Create list error:', error);
      setError(error.response?.data?.error || error.response?.data?.message || '创建列表失败');
      setAddingToList(false);
      return null;
    }
  };

  // 将搜索结果添加到 Leads List
  const handleAddToLeadsList = async (overrideListId) => {
    const listId = overrideListId || selectedListId;
    if (!listId) {
      setError('请选择一个列表或创建新列表');
      return;
    }

    // 只加用户勾选的(默认全选,用户可取消低分的)
    const selectedResults = results.filter(p => selectedUrls.has(p.linkedin_url || p.profile_url));
    if (selectedResults.length === 0) {
      setError('请至少选中一个候选人');
      return;
    }

    setAddingToList(true);
    setError('');
    setSuccess('');

    try {
      // 1. 首先将选中的搜索结果创建为 leads（如果不存在）
      const leadsData = selectedResults.map(profile => ({
        profile_url: profile.linkedin_url || profile.profile_url || '',
        provider_id: profile.provider_id || null,
        public_identifier: profile.public_identifier || null,
        name: profile.name || null,
        headline: profile.headline || null,
        location: profile.location || null,
        company: profile.company || null
      })).filter(lead => lead.profile_url); // 过滤掉没有 profile_url 的

      if (leadsData.length === 0) {
        setError('搜索结果中没有有效的 LinkedIn 链接');
        setAddingToList(false);
        return;
      }

      // 来源:全部勾选的候选人都来自 CSV 时打 'csv',否则归到 'search'(URL / NLP 搜索)
      const allFromCsv = selectedResults.length > 0 && selectedResults.every(p => p._source === 'csv');
      const createResponse = await axios.post(`${API_BASE_URL}/leads/batch`, {
        account_id: accountId,
        leads: leadsData,
        source: allFromCsv ? 'csv' : 'search'
      });

      if (!createResponse.data.success) {
        throw new Error('创建 leads 失败');
      }

      // batch 接口现在直接返回所有 leads(含已存在的),每个对象都带 id
      // 不再需要 GET /leads 全表反查
      const leadIds = (createResponse.data.leads || [])
        .map(l => l.id)
        .filter(id => id != null);

      if (leadIds.length === 0) {
        setError('无法找到创建的 leads');
        setAddingToList(false);
        return;
      }

      // 将 leads 添加到选定的 list
      const addResponse = await axios.post(`${API_BASE_URL}/leads-lists/${listId}/leads`, {
        lead_ids: leadIds
      });

      if (addResponse.data.success) {
        setSuccess(`成功添加 ${addResponse.data.added} 个 leads 到列表！${addResponse.data.skipped > 0 ? `（跳过 ${addResponse.data.skipped} 个已存在的）` : ''}`);
        setShowAddToList(false);
        setSelectedListId('');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('添加到列表失败');
      }
    } catch (error) {
      console.error('Add to list error:', error);
      setError(error.response?.data?.error || error.message || '添加到列表失败');
    } finally {
      setAddingToList(false);
    }
  };

  return (
    <div className="search-page">
      <header className="search-header">
        <h1>🔍 LinkedIn 人才搜索</h1>
      </header>

      <div className="search-container">
        <div className="search-method-selector">
          <button
            className={`method-btn ${searchMethod === 'url' ? 'active' : ''}`}
            onClick={() => setSearchMethod('url')}
          >
            📎 URL 搜索
          </button>
          <button
            className={`method-btn ${searchMethod === 'nlp' ? 'active' : ''}`}
            onClick={() => setSearchMethod('nlp')}
          >
            💬 自然语言搜索
          </button>
          <button
            className={`method-btn ${searchMethod === 'csv' ? 'active' : ''}`}
            onClick={() => setSearchMethod('csv')}
          >
            📁 CSV / Excel 导入
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && (
          <div className="toast toast-success" role="status" aria-live="polite">
            <span className="toast-icon">✅</span>
            <span className="toast-message">{success}</span>
            <button
              type="button"
              className="toast-close"
              onClick={() => setSuccess('')}
              aria-label="关闭提示"
            >
              ×
            </button>
          </div>
        )}

        {searchMethod === 'url' && (
          <div className="search-form-container">
            <h2>使用 LinkedIn 搜索 URL</h2>
            <p className="description">
              请在LinkedIn上执行搜索，然后复制浏览器地址栏的完整URL
            </p>
            <form onSubmit={handleSearchByUrl}>
              <div className="form-group">
                <label htmlFor="searchUrl">LinkedIn 搜索 URL</label>
                <input
                  type="url"
                  id="searchUrl"
                  value={searchUrl}
                  onChange={(e) => setSearchUrl(e.target.value)}
                  placeholder="https://www.linkedin.com/search/results/people/..."
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxResults">提取人才数量 (1-1000)</label>
                <input
                  type="number"
                  id="maxResults"
                  value={maxResultsInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // 更新输入框显示值
                    setMaxResultsInput(value);
                    // 如果输入有效，更新实际值
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && value !== '') {
                      const clampedValue = Math.min(1000, Math.max(1, numValue));
                      setMaxResults(clampedValue);
                    }
                  }}
                  onBlur={(e) => {
                    // 失去焦点时，验证并修正值
                    const value = e.target.value;
                    const numValue = parseInt(value, 10);
                    if (isNaN(numValue) || value === '' || numValue < 1) {
                      setMaxResultsInput('10');
                      setMaxResults(10);
                    } else if (numValue > 1000) {
                      setMaxResultsInput('1000');
                      setMaxResults(1000);
                    } else {
                      setMaxResultsInput(String(numValue));
                      setMaxResults(numValue);
                    }
                  }}
                  min="1"
                  max="1000"
                  required
                  disabled={loading}
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    搜索中...
                  </>
                ) : (
                  '开始搜索'
                )}
              </button>
            </form>
          </div>
        )}

        {searchMethod === 'nlp' && (
          <>
            {!showConfirmation ? (
              <div className="search-form-container">
                <h2>使用自然语言搜索</h2>
                <p className="description">
                  用自然语言描述你想找的人才，AI 将帮你构建搜索条件
                </p>
                <form onSubmit={handleSearchByNLP}>
                  <div className="form-group">
                    <label htmlFor="searchQuery">搜索描述</label>
                    <textarea
                      id="searchQuery"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="例如：我想找在北京的资深Python工程师，有5年以上工作经验"
                      rows="4"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ marginBottom: '8px' }}>
                      人脉度数
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                        默认全选 = 不过滤
                      </span>
                    </label>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {[
                        { value: 1, label: '1 度（已连接）' },
                        { value: 2, label: '2 度' },
                        { value: 3, label: '3 度 +' }
                      ].map(({ value, label }) => {
                        const checked = networkDistance.includes(value);
                        return (
                          <label
                            key={value}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              border: `1.5px solid ${checked ? '#8b5cf6' : '#e5e7eb'}`,
                              background: checked ? '#f5f3ff' : '#fff',
                              borderRadius: '8px',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.5 : 1,
                              userSelect: 'none',
                              whiteSpace: 'nowrap',
                              fontSize: '14px',
                              color: checked ? '#6d28d9' : '#374151',
                              fontWeight: checked ? 600 : 400,
                              transition: 'all 0.15s ease',
                              boxShadow: checked ? '0 1px 3px rgba(139, 92, 246, 0.15)' : 'none'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={loading}
                              style={{ accentColor: '#8b5cf6', cursor: loading ? 'not-allowed' : 'pointer' }}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNetworkDistance([...networkDistance, value].sort());
                                } else {
                                  // 不允许全部取消,至少保留一个
                                  const next = networkDistance.filter(d => d !== value);
                                  if (next.length === 0) return;
                                  setNetworkDistance(next);
                                }
                              }}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="maxResultsNLP">提取人才数量 (1-1000)</label>
                    <input
                      type="number"
                      id="maxResultsNLP"
                      value={maxResultsInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        // 更新输入框显示值
                        setMaxResultsInput(value);
                        // 如果输入有效，更新实际值
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && value !== '') {
                          const clampedValue = Math.min(1000, Math.max(1, numValue));
                          setMaxResults(clampedValue);
                        }
                      }}
                      onBlur={(e) => {
                        // 失去焦点时，验证并修正值
                        const value = e.target.value;
                        const numValue = parseInt(value, 10);
                        if (isNaN(numValue) || value === '' || numValue < 1) {
                          setMaxResultsInput('10');
                          setMaxResults(10);
                        } else if (numValue > 1000) {
                          setMaxResultsInput('1000');
                          setMaxResults(1000);
                        } else {
                          setMaxResultsInput(String(numValue));
                          setMaxResults(numValue);
                        }
                      }}
                      min="1"
                      max="1000"
                      required
                      disabled={loading}
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        AI 解析中...
                      </>
                    ) : (
                      '解析搜索条件'
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="confirmation-container">
                <h2>✅ 确认搜索条件</h2>
                <p className="description">
                  AI 已将您的查询转换为以下搜索条件，请确认后执行搜索
                </p>

                <div className="original-query">
                  <h3>原始查询</h3>
                  <p className="query-text">{originalQuery}</p>
                </div>

                {linkedinSearchUrl && (
                  <div className="linkedin-url-container">
                    <h3>🔗 LinkedIn 搜索 URL</h3>
                    <div className="url-box">
                      <input
                        type="text"
                        value={linkedinSearchUrl}
                        readOnly
                        className="url-input"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        className="btn-copy"
                        onClick={() => {
                          navigator.clipboard.writeText(linkedinSearchUrl);
                          setSuccess('URL已复制到剪贴板！');
                          setTimeout(() => setSuccess(''), 2000);
                        }}
                      >
                        📋 复制
                      </button>
                      <a
                        href={linkedinSearchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-open"
                      >
                        🔗 在LinkedIn中打开
                      </a>
                    </div>
                  </div>
                )}

                <div className="converted-info">
                  <details open={searchExecutionMode === 'parameters'}>
                    <summary>执行模式</summary>
                    <pre>{JSON.stringify({
                      mode: searchExecutionMode,
                      reason: parameterSearchReason || null,
                      account_capabilities: accountCapabilities
                    }, null, 2)}</pre>
                  </details>
                </div>

                <div className="parsed-params">
                  <h3>解析的搜索条件</h3>
                  <div className="params-grid">
                    {parsedParams && Object.entries(parsedParams)
                      .filter(([key]) => key !== 'preferred_service')
                      .map(([key, value]) => (
                      <div key={key} className="param-item">
                        <label className="param-label">
                          {getParamLabel(key)}:
                        </label>
                        <div className="param-value">
                          {key === 'profile_language' ?
                            formatProfileLanguage(value) :
                            (typeof value === 'object' ? JSON.stringify(value) : String(value))
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="parsed-params">
                  <h3>搜索执行信息</h3>
                  <div className="params-grid">
                    <div className="param-item">
                      <label className="param-label">搜索方式:</label>
                      <div className="param-value">
                        {getSearchServiceLabel(parsedParams?.preferred_service)}
                      </div>
                    </div>
                    <div className="param-item">
                      <label className="param-label">账号能力:</label>
                      <div className="param-value">
                        {getAccountCapabilityLabel(accountCapabilities)}
                      </div>
                    </div>
                    <div className="param-item">
                      <label className="param-label">执行方式:</label>
                      <div className="param-value">
                        {getExecutionModeLabel(searchExecutionMode)}
                      </div>
                    </div>
                  </div>
                </div>

                {unsupportedFilters.length > 0 && (
                  <div className="converted-info">
                    <details open>
                      <summary>未写入 LinkedIn URL 的筛选项</summary>
                      <pre>{JSON.stringify(unsupportedFilters, null, 2)}</pre>
                    </details>
                  </div>
                )}

                <div className="converted-info">
                  <details>
                    <summary>查看 LinkedIn URL 参数</summary>
                    <pre>{JSON.stringify(urlParams, null, 2)}</pre>
                  </details>
                </div>

                <div className="converted-info">
                  <details>
                    <summary>查看已解析的筛选 ID</summary>
                    <pre>{JSON.stringify(resolvedFilters, null, 2)}</pre>
                  </details>
                </div>

                <div className="converted-info">
                  <details>
                    <summary>查看转换后的技术参数</summary>
                    <pre>{JSON.stringify(convertedParams, null, 2)}</pre>
                  </details>
                </div>

                <div className="confirmation-actions">
                  <button 
                    className="btn btn-secondary" 
                    onClick={handleCancelConfirmation}
                    disabled={loading}
                  >
                    ✏️ 返回修改
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleConfirmSearch}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        搜索中...
                      </>
                    ) : (
                      '✓ 确认并搜索'
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {searchMethod === 'csv' && (
          <div className="search-form-container">
            <h2>从 CSV / Excel 导入候选人</h2>
            <p className="description">
              支持 <strong>.csv</strong> 和 <strong>.xlsx</strong>。<strong>必需列</strong>:LinkedIn URL(支持别名 url / profile_url / linkedin)。
              <strong>强烈推荐</strong>:name(或 first_name + last_name)——消息模板里的 {'{first_name}'} 需要它。
              其他列(headline / location / company)可选。
            </p>

            <div className="csv-upload-zone">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                id="csvFileInput"
                style={{ display: 'none' }}
                onChange={(e) => handleCsvFile(e.target.files?.[0])}
                disabled={csvParsing}
              />
              <label htmlFor="csvFileInput" className={`csv-upload-label ${csvParsing ? 'csv-upload-label-disabled' : ''}`}>
                {csvParsing ? (
                  <>
                    <span className="spinner"></span>
                    <span>解析中...</span>
                  </>
                ) : csvFileName ? (
                  <>
                    <span className="csv-upload-icon">📄</span>
                    <span className="csv-upload-filename">{csvFileName}</span>
                    <span className="csv-upload-hint">(点击重新选择)</span>
                  </>
                ) : (
                  <>
                    <span className="csv-upload-icon">📁</span>
                    <span>点击选择 CSV / Excel 文件</span>
                    <span className="csv-upload-hint">(最大 5MB)</span>
                  </>
                )}
              </label>
            </div>

            {csvError && <div className="alert alert-error">{csvError}</div>}

            {csvStats && (
              <div className="csv-stats">
                <div className="csv-stats-row">
                  <span className="csv-stat csv-stat-ok">
                    ✅ 识别 <strong>{csvStats.imported}</strong> 行有效数据
                  </span>
                  {csvStats.withName > 0 && (
                    <span className="csv-stat csv-stat-ok">
                      含姓名 <strong>{csvStats.withName}</strong>
                    </span>
                  )}
                  {csvStats.withoutName > 0 && (
                    <span className="csv-stat csv-stat-warn" title="模板里 {first_name} 将渲染为空,可考虑先打分以补全">
                      ⚠️ 缺名 <strong>{csvStats.withoutName}</strong>
                    </span>
                  )}
                  {csvStats.duplicates > 0 && (
                    <span className="csv-stat csv-stat-info">
                      去重 <strong>{csvStats.duplicates}</strong>
                    </span>
                  )}
                  {csvStats.invalidUrls > 0 && (
                    <span className="csv-stat csv-stat-warn">
                      非 LinkedIn URL 跳过 <strong>{csvStats.invalidUrls}</strong>
                    </span>
                  )}
                </div>
              </div>
            )}

            {csvParsedRows.length > 0 && (
              <div className="csv-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCsvImport}
                >
                  📥 导入 {csvParsedRows.length} 个到搜索结果区
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCsvReset}
                >
                  重新选择
                </button>
              </div>
            )}

            <details className="csv-format-help">
              <summary>📖 表头别名说明(CSV / Excel 通用)</summary>
              <table className="csv-alias-table">
                <thead>
                  <tr><th>系统字段</th><th>识别的 CSV 表头</th><th>必需</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>LinkedIn URL</td>
                    <td><code>{CSV_HEADER_ALIASES.linkedin_url.join(' / ')}</code></td>
                    <td>✅ 必需</td>
                  </tr>
                  <tr>
                    <td>姓名</td>
                    <td><code>{CSV_HEADER_ALIASES.name.join(' / ')}</code></td>
                    <td>🟡 推荐</td>
                  </tr>
                  <tr>
                    <td>名 / 姓 (拆分列)</td>
                    <td><code>{CSV_HEADER_ALIASES.first_name.join(' / ')}</code> + <code>{CSV_HEADER_ALIASES.last_name.join(' / ')}</code></td>
                    <td>🟡 替代姓名</td>
                  </tr>
                  <tr>
                    <td>职位 / 标题</td>
                    <td><code>{CSV_HEADER_ALIASES.headline.join(' / ')}</code></td>
                    <td>可选</td>
                  </tr>
                  <tr>
                    <td>地点</td>
                    <td><code>{CSV_HEADER_ALIASES.location.join(' / ')}</code></td>
                    <td>可选</td>
                  </tr>
                  <tr>
                    <td>公司</td>
                    <td><code>{CSV_HEADER_ALIASES.company.join(' / ')}</code></td>
                    <td>可选</td>
                  </tr>
                </tbody>
              </table>
              <p className="csv-format-note">
                只有 URL 一列也能导入,然后用 AI 打分会自动补全 headline / location / company。
                按 LinkedIn URL 自动去重。
              </p>
            </details>
          </div>
        )}

        {results.length > 0 && (
          <div className="results-container">
            <div className="results-header">
              <h2>搜索结果 ({results.length},已选 {selectedUrls.size})</h2>
              <div className="results-actions">
                <div className="results-actions-group">
                  <button className="btn-chip" onClick={selectAll} disabled={selectedUrls.size === results.length}>
                    全选
                  </button>
                  <button className="btn-chip" onClick={deselectAll} disabled={selectedUrls.size === 0}>
                    取消全选
                  </button>
                  <span
                    className={`threshold-picker ${Object.keys(scores).length === 0 ? 'threshold-picker-disabled' : ''}`}
                    title={Object.keys(scores).length === 0 ? '先点击「开始打分」后才能按分数筛选' : '一键选中分数 ≥ 阈值的候选人(覆盖当前选中)'}
                  >
                    <span className="threshold-prefix">≥</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={scoreThreshold}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setScoreThreshold(isNaN(v) ? 0 : Math.max(0, Math.min(100, v)));
                      }}
                      disabled={Object.keys(scores).length === 0}
                      className="threshold-input"
                    />
                    <span className="threshold-suffix">分</span>
                    <button
                      className="threshold-apply-btn"
                      onClick={selectByScore}
                      disabled={Object.keys(scores).length === 0}
                    >
                      选高分
                    </button>
                  </span>
                </div>
                <div className="results-actions-group">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddToList(true)}
                    disabled={selectedUrls.size === 0}
                  >
                    📝 添加 {selectedUrls.size} 个到 Leads List
                  </button>
                  <button className="btn btn-secondary" onClick={downloadResults}>
                    📥 下载 JSON
                  </button>
                </div>
              </div>
            </div>

            {/* === 打分区 === */}
            <div className="scoring-section">
              <div className="scoring-input-row">
                <label className="scoring-label" htmlFor="hiringNeed">
                  🎯 招聘需求 (用于 AI 打分,可不填,直接走加 Lead 流程)
                </label>
                <textarea
                  id="hiringNeed"
                  className="scoring-textarea"
                  value={hiringNeed}
                  onChange={(e) => setHiringNeed(e.target.value)}
                  placeholder="例如:5+ 年 Python AI 工程师经验,熟悉 LLM / 向量数据库 / 微服务架构。在硅谷/纽约。"
                  rows={3}
                  disabled={scoring}
                />
              </div>
              <div className="scoring-actions">
                {!scoring ? (
                  <button
                    className="btn btn-primary"
                    onClick={handleStartScoring}
                    disabled={!hiringNeed.trim() || results.length === 0}
                  >
                    🚀 开始打分 ({results.length} 人)
                  </button>
                ) : (
                  <button className="btn btn-secondary" onClick={handleStopScoring}>
                    ⏹ 停止打分
                  </button>
                )}
                {Object.keys(scores).length > 0 && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSortByScore(s => !s)}
                    title="按分数从高到低排序"
                  >
                    {sortByScore ? '↕ 恢复原顺序' : '↓ 按分数排序'}
                  </button>
                )}
                {scoring && (
                  <span className="scoring-progress-text">
                    打分中... {Object.keys(scores).length}/{results.length}
                  </span>
                )}
              </div>
              {scoringError && <div className="alert alert-error">{scoringError}</div>}
              {parsedCriteria && parsedCriteria.length > 0 && (
                <details className="scoring-criteria">
                  <summary>评分维度 ({parsedCriteria.length} 项)</summary>
                  <ul>
                    {parsedCriteria.map(c => (
                      <li key={c.id}>
                        <strong>权重 {c.weight}%</strong> · {c.criterion}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            <div className="results-list">
              {(sortByScore
                ? [...results].sort((a, b) => {
                    const sa = scores[a.linkedin_url || a.profile_url]?.score ?? -1;
                    const sb = scores[b.linkedin_url || b.profile_url]?.score ?? -1;
                    return sb - sa;
                  })
                : results
              ).map((profile, index) => {
                const url = profile.linkedin_url || profile.profile_url;
                const scoreInfo = scores[url];
                const status = statusByUrl[url];
                const inProgress = status === 'crawling' || status === 'scoring';
                return (
                  <div key={url || index} className={`profile-card ${url && !selectedUrls.has(url) ? 'profile-card-unselected' : ''}`}>
                    <div className="profile-header">
                      <label className="profile-select-label" title={selectedUrls.has(url) ? '取消选中(不加入 Leads List)' : '选中(加入 Leads List)'}>
                        <input
                          type="checkbox"
                          className="profile-select-checkbox"
                          checked={!!url && selectedUrls.has(url)}
                          onChange={() => toggleSelected(url)}
                          disabled={!url}
                        />
                      </label>
                      <h3>
                        {profile.name || '未知姓名'}
                        {scoreInfo && (
                          <span
                            className="score-badge"
                            style={{ backgroundColor: scoreColor(scoreInfo.score) }}
                            title={scoreInfo.reason}
                          >
                            {scoreInfo.score}
                          </span>
                        )}
                        {inProgress && (
                          <span className="score-badge score-badge-loading">
                            <span className="spinner"></span>
                            {status === 'crawling' ? '爬取中' : '打分中'}
                          </span>
                        )}
                      </h3>
                      {profile.linkedin_url && (
                        <a
                          href={profile.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="profile-link"
                        >
                          查看 LinkedIn
                        </a>
                      )}
                    </div>

                    <div className="profile-details">
                      {profile.headline && (
                        <p className="profile-headline">{profile.headline}</p>
                      )}
                      {profile.location && (
                        <p className="profile-location">📍 {profile.location}</p>
                      )}
                      {profile.company && (
                        <p className="profile-company">🏢 {profile.company}</p>
                      )}
                    </div>

                    {scoreInfo && (
                      <div className="score-detail">
                        <p className="score-reason">
                          <strong>评分理由:</strong>{' '}
                          <span dangerouslySetInnerHTML={{
                            __html: (scoreInfo.reason || '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
                          }} />
                        </p>
                        {Array.isArray(scoreInfo.criteria_breakdown) && scoreInfo.criteria_breakdown.length > 0 && (
                          <div className="score-breakdown">
                            {scoreInfo.criteria_breakdown.map(c => {
                              const criterion = parsedCriteria?.find(p => p.id === c.id);
                              return (
                                <div key={c.id} className="score-breakdown-item">
                                  <span className="breakdown-label">
                                    {criterion ? `${criterion.criterion.slice(0, 40)}${criterion.criterion.length > 40 ? '…' : ''}` : `维度 ${c.id}`}
                                  </span>
                                  <span className="breakdown-score" style={{ color: scoreColor(c.score) }}>
                                    {c.score}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    <details className="profile-json">
                      <summary>查看完整资料 (JSON)</summary>
                      <pre>{JSON.stringify(profile, null, 2)}</pre>
                    </details>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add to Leads List Modal */}
      {showAddToList && (
        <div className="modal-overlay" onClick={() => !addingToList && setShowAddToList(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>添加到 Leads List</h2>
            <p className="modal-description">
              将选中的 {selectedUrls.size} 个候选人添加到 Leads List
            </p>

            {!showCreateList ? (
              <>
                <div className="form-group">
                  <label htmlFor="selectList">选择列表</label>
                  <select
                    id="selectList"
                    value={selectedListId}
                    onChange={(e) => setSelectedListId(e.target.value)}
                    className="form-select"
                    disabled={addingToList}
                  >
                    <option value="">-- 请选择列表 --</option>
                    {lists.map(list => (
                      <option key={list.id} value={list.id}>
                        {list.name} ({list.lead_count || 0} leads)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateList(true);
                      setSelectedListId('');
                    }}
                    disabled={addingToList}
                  >
                    ➕ 创建新列表
                  </button>
                  <div className="modal-actions-right">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddToList(false);
                        setSelectedListId('');
                        setError('');
                      }}
                      disabled={addingToList}
                    >
                      取消
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAddToLeadsList()}
                      disabled={addingToList || !selectedListId}
                    >
                      {addingToList ? (
                        <>
                          <span className="spinner"></span>
                          添加中...
                        </>
                      ) : (
                        '确认添加'
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="newListName">列表名称 *</label>
                  <input
                    type="text"
                    id="newListName"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="例如：Python 工程师"
                    disabled={addingToList}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newListDescription">列表描述（可选）</label>
                  <textarea
                    id="newListDescription"
                    value={newListDescription}
                    onChange={(e) => setNewListDescription(e.target.value)}
                    placeholder="描述这个列表的用途..."
                    rows="3"
                    disabled={addingToList}
                    className="form-textarea"
                  />
                </div>

                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateList(false);
                      setNewListName('');
                      setNewListDescription('');
                    }}
                    disabled={addingToList}
                  >
                    返回选择列表
                  </button>
                  <div className="modal-actions-right">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddToList(false);
                        setShowCreateList(false);
                        setSelectedListId('');
                        setNewListName('');
                        setNewListDescription('');
                        setError('');
                      }}
                      disabled={addingToList}
                    >
                      取消
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={async () => {
                        if (!newListName.trim()) {
                          setError('请输入列表名称');
                          return;
                        }
                        
                        try {
                          const newListId = await handleCreateList();
                          if (newListId) {
                            await handleAddToLeadsList(newListId);
                            // 添加成功后关闭模态框
                            setShowAddToList(false);
                            setShowCreateList(false);
                          }
                        } catch (error) {
                          console.error('Create and add error:', error);
                          // 错误已在 handleCreateList 或 handleAddToLeadsList 中处理
                        }
                      }}
                      disabled={addingToList || !newListName.trim()}
                    >
                      {addingToList ? (
                        <>
                          <span className="spinner"></span>
                          创建中...
                        </>
                      ) : (
                        '创建并添加'
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchPage;
